import React, { useState, useRef, useContext } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
  ActivityIndicator,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import { useAppSelector, useAppDispatch } from '../../../redux/helper';
import { fs, s, iconSize } from '../../../utils/libs';
import { isEmployeeAdminRole } from '../../../utils/roles';
import { formatHeaderLocationName } from '../../../utils/headerLocationName';
import { BottomSheetModal } from '@gorhom/bottom-sheet';
import LocationBottomSheet from '../../LocationPicker/LocationBottomSheet';
import {
  LocationSheetFooter,
  LocationSheetList,
} from '../../LocationPicker/LocationSheetList';
import ErrorContext from '../../../Context/ErrorContext';
import { useNavigation, useRoute } from '@react-navigation/native';
import { isAllLocationsEligibleRoute } from '../../../hooks/useAllLocationsRoute';
import {
  setIsAllLocations,
  setLocationSwitching,
  setCurrentLocationId,
  setCurrentLocation,
  incrementLocationVersion,
} from '../../../redux/locationSlice';
import useLocation from '../../../hooks/useLocation';

import {
  clearOwnerLocationState,
  clearEmployeeLocationState,
} from '../../../services/locationSwitchCleanup';

interface BackOfficeHeaderLocationPickerProps {
  textColor?: string;
  containerStyle?: StyleProp<ViewStyle>;
}

const BackOfficeHeaderLocationPicker: React.FC<
  BackOfficeHeaderLocationPickerProps
> = ({ textColor = '#fff', containerStyle }) => {
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const route = useRoute();
  const { handleError } = useContext(ErrorContext);
  const bottomSheetRef = useRef<BottomSheetModal | null>(null);
  const [modalVisible, setModalVisible] = useState(false);

  const currentLocation = useAppSelector(s => s.location.currentLocation);
  const currentLocationId = useAppSelector(s => s.location.currentLocationId);
  const locations = useAppSelector(s => s.location.locations);
  const isAllLocations = useAppSelector(s => s.location.isAllLocations);
  const isLocationSwitching = useAppSelector(
    s => s.location.isLocationSwitching,
  );
  const employeedata = useAppSelector(
    state => state.auth.inApp.account.employeeInfo,
  );

  const { switchLocation, isAdmin } = useLocation();

  // Check if current route supports "All Locations" option
  const showAllLocationsOption = isAllLocationsEligibleRoute(
    route.name,
    locations,
  );

  // Don't render if user is not admin
  if (!isAdmin) {
    return null;
  }

  const handleSelectAllLocations = async () => {
    setModalVisible(false);
    bottomSheetRef.current?.dismiss();

    // If already in all locations mode, do nothing
    if (isAllLocations) {
      return;
    }

    dispatch(setLocationSwitching(true));

    try {
      // Clear state first - this clears all cached data
      clearOwnerLocationState(dispatch);

      // Wait for React to process the clear actions
      await new Promise(resolve => setTimeout(resolve, 150));

      // Now set the flag (this also increments locationVersion which triggers refetches)
      dispatch(setIsAllLocations(true));

      // Wait for screens to start their fetch operations
      await new Promise(resolve => setTimeout(resolve, 300));
    } finally {
      dispatch(setLocationSwitching(false));
    }
  };

  const handleSelectLocation = async (locationId: string) => {
    setModalVisible(false);
    bottomSheetRef.current?.dismiss();

    // Check if trying to switch to the same location (and not in all locations mode)
    if (!isAllLocations && currentLocationId === locationId) {
      return;
    }

    // Check permissions
    if (!isEmployeeAdminRole(employeedata?.bp_employee_role)) {
      handleError(
        'You do not have permission to switch locations.',
        'error',
        true,
      );
      return;
    }

    dispatch(setLocationSwitching(true));

    try {
      // Turn off all locations mode if it was on
      if (isAllLocations) {
        dispatch(setIsAllLocations(false));
      }

      // Switch location via API
      await switchLocation(locationId);

      // Clear cached state tied to the location just left: backoffice data,
      // plus the POS cart and checkout, so a cart built at the previous
      // location does not survive the switch into the new one.
      clearOwnerLocationState(dispatch);
      clearEmployeeLocationState(dispatch);

      // Wait for React to process
      await new Promise(resolve => setTimeout(resolve, 150));

      // Trigger refetches
      dispatch(incrementLocationVersion());

      // Wait for screens to start fetching
      await new Promise(resolve => setTimeout(resolve, 300));
    } catch (error) {
      console.error('Location switch error:', error);
      handleError(
        'Failed to switch location. Please try again.',
        'error',
        true,
      );
    } finally {
      dispatch(setLocationSwitching(false));
    }
  };

  const displayText = isAllLocations
    ? 'All Locations'
    : currentLocation?.bp_location_name || 'Select Location';

  const locationLabel = formatHeaderLocationName(displayText, {
    isWeb: Platform.OS === 'web',
    picker: 'backOffice',
  });

  return (
    <View style={[styles.container, containerStyle]}>
      <TouchableOpacity
        style={styles.selector}
        onPress={() => {
          setModalVisible(true);
          requestAnimationFrame(() => bottomSheetRef.current?.present?.());
        }}
        disabled={isLocationSwitching}>
        {isAllLocations && (
          <FontAwesome6
            name="layer-group"
            iconStyle="solid"
            size={iconSize(14)}
            color={textColor}
            style={styles.allLocationsIcon}
          />
        )}
        <Text
          style={[
            styles.selectorText,
            { color: textColor, fontSize: fs(locationLabel.fontSize) },
          ]}
          numberOfLines={1}>
          {locationLabel.text}
        </Text>
        {isLocationSwitching ? (
          <ActivityIndicator size="small" color={textColor} />
        ) : (
          <FontAwesome6
            name="chevron-down"
            iconStyle="solid"
            size={iconSize(12)}
            color={textColor}
          />
        )}
      </TouchableOpacity>

      <LocationBottomSheet
        bottomSheetRef={bottomSheetRef}
        title="Select Location"
        onClose={() => setModalVisible(false)}
        footer={
          isEmployeeAdminRole(employeedata?.bp_employee_role) ? (
            <LocationSheetFooter
              onAddBusiness={() => {
                setModalVisible(false);
                bottomSheetRef.current?.dismiss();
                navigation.navigate('NewLocation');
              }}
            />
          ) : null
        }>
        <LocationSheetList
          locations={locations}
          currentLocationId={currentLocationId}
          isAllLocations={isAllLocations}
          showAllLocationsOption={showAllLocationsOption}
          onSelectLocation={handleSelectLocation}
          onSelectAllLocations={handleSelectAllLocations}
        />
      </LocationBottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginLeft: s(16),
  },
  selector: {
    flexDirection: 'row',
    alignItems: 'center',
    // paddingHorizontal: s(12),
    paddingVertical: s(6),
    gap: s(8),
  },
  selectorText: {
    lineHeight: fs(20),
    // fontFamily: GTFonts.semibold,
  },
  allLocationsIcon: {
    marginRight: s(-4),
  },
});

export default BackOfficeHeaderLocationPicker;
